import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, Plus, Trash2, CreditCard, Building2, Copy, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Datos bancarios para SPEI
const DATOS_BANCARIOS = {
  banco: "BBVA",
  titular: "Virginia Ivette Becerra Vadillo",
  clabe: "012320004892269587",
  cuenta: "0489226958",
  concepto: "Boleto Aniversario Zuno"
};

const ComprarPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [metodoPago, setMetodoPago] = useState("spei"); // "tarjeta" o "spei"
  const [ordenCreada, setOrdenCreada] = useState(null);
  const [copiado, setCopiado] = useState("");
  const [formData, setFormData] = useState({
    nombre_completo: "",
    email: "",
    telefono: "",
    tipo_comprador: "Exalumno 93-96",
    cantidad_boletos: 1,
    asistentes_adicionales: []
  });

  const precio_unitario = 500; // Precio por boleto en MXN
  
  // Comisión de Stripe: 3.6% + $3 MXN
  const STRIPE_PORCENTAJE = 0.036;
  const STRIPE_FIJO = 3;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === "cantidad_boletos" ? parseInt(value) || 1 : value
    }));
  };

  const handleTipoCompradorChange = (value) => {
    setFormData(prev => ({ ...prev, tipo_comprador: value }));
  };

  const agregarAsistente = () => {
    setFormData(prev => ({
      ...prev,
      asistentes_adicionales: [
        ...prev.asistentes_adicionales,
        { nombre: "", tipo: "Invitado" }
      ]
    }));
  };

  const eliminarAsistente = (index) => {
    setFormData(prev => ({
      ...prev,
      asistentes_adicionales: prev.asistentes_adicionales.filter((_, i) => i !== index)
    }));
  };

  const actualizarAsistente = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      asistentes_adicionales: prev.asistentes_adicionales.map((asistente, i) =>
        i === index ? { ...asistente, [field]: value } : asistente
      )
    }));
  };

  const calcularTotal = () => {
    let subtotal = 0;
    
    // Precio del comprador principal
    if (formData.tipo_comprador !== "Maestro") {
      subtotal += precio_unitario;
    }
    
    // Precios de asistentes adicionales
    formData.asistentes_adicionales.forEach(asistente => {
      if (asistente.tipo !== "Maestro") {
        subtotal += precio_unitario;
      }
    });
    
    return subtotal;
  };

  const calcularComisionStripe = () => {
    const subtotal = calcularTotal();
    if (subtotal === 0 || metodoPago !== "tarjeta") return 0;
    // Comisión = (subtotal * 3.6%) + $3
    return Math.round((subtotal * STRIPE_PORCENTAJE) + STRIPE_FIJO);
  };

  const calcularTotalConComision = () => {
    const subtotal = calcularTotal();
    if (metodoPago === "tarjeta") {
      return subtotal + calcularComisionStripe();
    }
    return subtotal;
  };

  const calcularSubtotalBoletos = () => {
    let subtotal = 0;
    if (formData.tipo_comprador !== "Maestro") {
      subtotal += precio_unitario;
    }
    formData.asistentes_adicionales.forEach(asistente => {
      if (asistente.tipo !== "Maestro") {
        subtotal += precio_unitario;
      }
    });
    return subtotal;
  };

  const subtotalBoletos = calcularTotal();
  const comisionStripe = calcularComisionStripe();
  const total = calcularTotalConComision();
  const totalBoletos = 1 + formData.asistentes_adicionales.length;

  const copiarAlPortapapeles = (texto, campo) => {
    navigator.clipboard.writeText(texto);
    setCopiado(campo);
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopiado(""), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre_completo || !formData.email || !formData.telefono) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    // Validar que todos los asistentes adicionales tengan nombre
    if (formData.asistentes_adicionales.length > 0) {
      const hayNombresVacios = formData.asistentes_adicionales.some(a => !a.nombre.trim());
      if (hayNombresVacios) {
        toast.error("Por favor completa los nombres de todos los asistentes");
        return;
      }
    }

    setLoading(true);

    try {
      // Si es pago con SPEI (sin comisión)
      if (metodoPago === "spei" && subtotalBoletos > 0) {
        const response = await axios.post(`${API}/checkout/spei`, {
          ...formData,
          cantidad_boletos: totalBoletos,
          total: subtotalBoletos  // SPEI no tiene comisión
        });
        
        setOrdenCreada({
          id: response.data.orden_id,
          referencia: response.data.referencia,
          total: subtotalBoletos
        });
        toast.success("Orden creada. Realiza tu transferencia SPEI.");
        setLoading(false);
        return;
      }

      // Pago con tarjeta (Stripe) - incluye comisión
      const response = await axios.post(`${API}/checkout/crear`, {
        ...formData,
        cantidad_boletos: totalBoletos,
        total_con_comision: total  // Enviar total con comisión
      });
      
      // Si todos son maestros (sin pago)
      if (response.data.sin_pago) {
        localStorage.setItem("compradorId", response.data.comprador_id);
        toast.success("Boletos generados exitosamente (sin costo)");
        navigate(`/mis-boletos/${response.data.comprador_id}`);
      } else {
        // Guardar comprador_id en localStorage
        localStorage.setItem("compradorId", response.data.comprador_id);
        
        // Redirigir a Stripe
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creando checkout:", error);
      toast.error("Error al procesar la solicitud. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-bone py-12">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-5xl">
        <Button
          onClick={() => navigate("/")}
          variant="ghost"
          className="mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2" size={20} />
          Volver al Inicio
        </Button>

        <div className="glass-card rounded-3xl p-8 md:p-12">
          <h1 className="text-4xl md:text-5xl font-bold text-zuno-wine mb-4">
            Comprar Boletos
          </h1>
          <p className="text-lg text-slate-600 mb-8">
            Completa tus datos para continuar con la compra
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Datos del comprador principal */}
            <div className="bg-slate-50 rounded-xl p-6 space-y-4">
              <h3 className="text-xl font-semibold text-zuno-wine mb-4">Tus Datos</h3>
              
              <div>
                <Label htmlFor="nombre_completo">Nombre Completo *</Label>
                <Input
                  id="nombre_completo"
                  name="nombre_completo"
                  value={formData.nombre_completo}
                  onChange={handleChange}
                  placeholder="Juan Pérez García"
                  required
                  data-testid="input-nombre"
                />
              </div>

              <div>
                <Label htmlFor="email">Correo Electrónico *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="correo@ejemplo.com"
                  required
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label htmlFor="telefono">Teléfono *</Label>
                <Input
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  placeholder="33-1234-5678"
                  required
                  data-testid="input-telefono"
                />
              </div>

              <div>
                <Label htmlFor="tipo_comprador">Eres *</Label>
                <Select
                  value={formData.tipo_comprador}
                  onValueChange={handleTipoCompradorChange}
                >
                  <SelectTrigger data-testid="select-tipo-comprador">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Exalumno 93-96">Exalumno 93-96</SelectItem>
                    <SelectItem value="Maestro">Maestro</SelectItem>
                    <SelectItem value="Invitado">Invitado</SelectItem>
                  </SelectContent>
                </Select>
                {formData.tipo_comprador === "Maestro" && (
                  <p className="text-sm text-green-600 mt-2">✓ Boleto sin costo</p>
                )}
              </div>
            </div>

            {/* Asistentes adicionales */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-zuno-wine">
                  Asistentes Adicionales ({formData.asistentes_adicionales.length})
                </h3>
                <Button
                  type="button"
                  onClick={agregarAsistente}
                  variant="outline"
                  size="sm"
                  data-testid="add-attendee-btn"
                  disabled={formData.asistentes_adicionales.length >= 9}
                >
                  <Plus className="mr-2" size={16} />
                  Agregar Asistente
                </Button>
              </div>

              {formData.asistentes_adicionales.map((asistente, index) => (
                <div
                  key={`asistente-${index}-${asistente.nombre || 'new'}`}
                  className="bg-slate-50 rounded-xl p-6 space-y-4 relative"
                  data-testid={`attendee-${index}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-slate-700">
                      Asistente {index + 2}
                    </h4>
                    <Button
                      type="button"
                      onClick={() => eliminarAsistente(index)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500"
                      data-testid={`remove-attendee-${index}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>

                  <div>
                    <Label htmlFor={`asistente-nombre-${index}`}>Nombre Completo *</Label>
                    <Input
                      id={`asistente-nombre-${index}`}
                      value={asistente.nombre}
                      onChange={(e) => actualizarAsistente(index, "nombre", e.target.value)}
                      placeholder="Nombre del asistente"
                      required
                      data-testid={`input-attendee-name-${index}`}
                    />
                  </div>

                  <div>
                    <Label htmlFor={`asistente-tipo-${index}`}>Tipo *</Label>
                    <Select
                      value={asistente.tipo}
                      onValueChange={(value) => actualizarAsistente(index, "tipo", value)}
                    >
                      <SelectTrigger data-testid={`select-attendee-type-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Exalumno 93-96">Exalumno 93-96</SelectItem>
                        <SelectItem value="Maestro">Maestro</SelectItem>
                        <SelectItem value="Invitado">Invitado</SelectItem>
                      </SelectContent>
                    </Select>
                    {asistente.tipo === "Maestro" && (
                      <p className="text-sm text-green-600 mt-2">✓ Boleto sin costo</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Resumen */}
            <Card className="p-6 bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-200">
              <h3 className="text-lg font-semibold mb-4">Resumen de Compra</h3>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span>Total de boletos:</span>
                  <span className="font-semibold">{totalBoletos}</span>
                </div>
                <div className="flex justify-between">
                  <span>Precio por boleto:</span>
                  <span>${precio_unitario} MXN</span>
                </div>
                {formData.tipo_comprador === "Maestro" && (
                  <div className="flex justify-between text-green-600">
                    <span>Descuento (Maestro):</span>
                    <span>-${precio_unitario} MXN</span>
                  </div>
                )}
                {formData.asistentes_adicionales.filter(a => a.tipo === "Maestro").length > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>
                      Descuento ({formData.asistentes_adicionales.filter(a => a.tipo === "Maestro").length} Maestros):
                    </span>
                    <span>
                      -${formData.asistentes_adicionales.filter(a => a.tipo === "Maestro").length * precio_unitario} MXN
                    </span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span>Subtotal boletos:</span>
                  <span>${subtotalBoletos} MXN</span>
                </div>
                {metodoPago === "tarjeta" && comisionStripe > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>Comisión procesamiento (3.6% + $3):</span>
                    <span>${comisionStripe} MXN</span>
                  </div>
                )}
              </div>
              <div className="border-t-2 border-zuno-wine pt-4 flex justify-between items-center">
                <span className="text-2xl font-bold text-zuno-wine">Total:</span>
                <span className="text-3xl font-bold text-zuno-wine">
                  ${total} MXN {total === 0 && "(GRATIS)"}
                </span>
              </div>
            </Card>

            {/* Selector de método de pago - Solo SPEI */}
            {subtotalBoletos > 0 && !ordenCreada && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Método de Pago</Label>
                <div className="flex justify-center">
                  <div className="flex flex-col w-full max-w-sm">
                    <Button
                      type="button"
                      onClick={() => setMetodoPago("spei")}
                      variant="default"
                      className="h-auto py-4 flex flex-col items-center gap-2 bg-zuno-wine text-white"
                      data-testid="metodo-spei"
                    >
                      <Building2 size={28} />
                      <span className="font-semibold">Transferencia SPEI</span>
                      <span className="text-xs opacity-80">Sin comisión adicional</span>
                    </Button>
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      Recibirás los datos bancarios para realizar tu transferencia
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Mostrar datos bancarios si eligió SPEI y se creó la orden */}
            {ordenCreada && (
              <Card className="p-6 bg-blue-50 border-blue-200">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="text-green-600" size={28} />
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">¡Orden Creada!</h3>
                    <p className="text-sm text-slate-600">Referencia: <strong>{ordenCreada.referencia}</strong></p>
                  </div>
                </div>
                
                <p className="text-slate-700 mb-4">
                  Realiza tu transferencia SPEI con los siguientes datos. Una vez confirmado el pago, 
                  recibirás tus boletos por correo electrónico.
                </p>

                <div className="bg-white rounded-xl p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Banco:</span>
                    <span className="font-semibold">{DATOS_BANCARIOS.banco}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Titular:</span>
                    <span className="font-semibold">{DATOS_BANCARIOS.titular}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">CLABE:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold">{DATOS_BANCARIOS.clabe}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => copiarAlPortapapeles(DATOS_BANCARIOS.clabe, "clabe")}
                      >
                        {copiado === "clabe" ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Cuenta:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold">{DATOS_BANCARIOS.cuenta}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => copiarAlPortapapeles(DATOS_BANCARIOS.cuenta, "cuenta")}
                      >
                        {copiado === "cuenta" ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center border-t pt-3 mt-3">
                    <span className="text-slate-600">Monto a transferir:</span>
                    <span className="text-2xl font-bold text-zuno-wine">${ordenCreada.total} MXN</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Concepto/Referencia:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-zuno-wine">{ordenCreada.referencia}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => copiarAlPortapapeles(ordenCreada.referencia, "referencia")}
                      >
                        {copiado === "referencia" ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Importante:</strong> Usa la referencia <strong>{ordenCreada.referencia}</strong> en el concepto 
                    de tu transferencia para identificar tu pago. El proceso de verificación puede tomar de 1 a 24 horas.
                  </p>
                </div>

                <Button
                  type="button"
                  onClick={() => navigate("/")}
                  className="w-full mt-4 btn-primary"
                >
                  Volver al Inicio
                </Button>
              </Card>
            )}

            {!ordenCreada && (
              <Button
                type="submit"
                className="w-full btn-primary text-xl py-6"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  total === 0 ? "Generar Boletos Gratis" : "Generar Orden SPEI"
                )}
              </Button>
            )}
          </form>

          {total > 0 && !ordenCreada && (
            <p className="text-sm text-slate-500 mt-6 text-center">
              Se generará una orden con los datos para tu transferencia SPEI
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComprarPage;
